import { QUEUE_TYPE, SUBJECT_KEYS } from "@elixir/types";
import { Col, Modal, Row } from "antd";
import { GET_HUB_HOST, GET_LOGIN_HOST, setEnv } from "apis";
import dateFormat from "dateformat";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { getAllQueuesQuery } from "../apis/tryst/get-all-queues";
import { useMyAccessProfileV2 } from "../apis/tryst/get-my-access-profile";
import queryClient from "../instances/react-query-client";
import CustomiseYourQueue from "../molecules/cards/queue/customise-your-queue";
import AppointmentLogsDrawer from "../organisms/appointment-logs-darwer";
import { HprHfrOnboardingModal } from "../organisms/hpr-hfr-onboarding/hpr-hfr-onboarding-modal";
import PackagesAndTreatmentsPopupsAndDrawers from "../organisms/packages-and-treatments/packages-and-treatments-popups-and-drawers";
import BottomMarquee from "../organisms/queue/bottom-marquee";
import DoctorFilter from "../organisms/queue/doctor-filter";
import LeftQueuePanel from "../organisms/queue/left-queue-panel";
import QueueHeader from "../organisms/queue/queue-header";
import RightQueuePanel from "../organisms/queue/right-queue-panel";
import SendReminderModal from "../organisms/queue/send-reminder-modal";
import ReceiptV2 from "../organisms/receipt-v2";
import SaQuestionnaireDrawer from "../organisms/sa-questionnaire/sa-questionnaire-drawer";
import SelectSlotModal from "../organisms/select-slot-modal";
import WorkflowViewJourney from "../organisms/workflow-view/workflow-view-journey";
import { RendererParams } from "../react-app-env";
import useQueueStore from "../stores/queue-store";
import { TLeftQueuePanel } from "../stores/queue-store/types";
import useToolPaymentsStore from "../stores/tool-payments/tool-payments-store";
import { EKA_EVENT } from "../utils/bridges";
import { HIDE_RENEWAL_STRIP, IncomingEvents, REFRESH_APPT } from "../utils/bridges/events/types";
import {
  getCalendarConfigs,
  getEvitalsConfig,
  getMe,
  isAccessAvailable,
  navigate,
  showNewSignInStrip,
  showPurchaseEkaCreditsPopover,
} from "../utils/bridges/methods";
import { useDoctorStaffCheck } from "../utils/doctor-staff-check";
import { track } from "../utils/tracking";
import { initAptListener } from "../utils/firestore-utils/apt-listener-manager";
import { getAclViewClassName } from "@elixir/utils";
import getB from "../utils/bridges/EkaBridge";
import PlanRenewalStrip from "../molecules/tool-payments/common/plan-renewal-strip";
import moment from "moment";

const LS_NEW_SIGN_IN_STRIP = "newSignInStrip";

setEnv({
  clientId: "dr",
  env: import.meta.env.VITE_APP_ENV === "PROD" ? "PROD" : "DEV",
});

const ReferralStrip = () => {
  useEffect(() => {
    window.renderReferralStrip?.({ containerId: "referral-strip" });
  }, []);
  return <div className="dr-w-full" id="referral-strip"></div>
}

const QueueDashboard = ({
  routeParams,
}: PropsWithChildren<Partial<RendererParams>>) => {
  const { cId } = routeParams || { cId: "all" };
  const _clinicId = cId;
  const { data: myAccessProfile } = useMyAccessProfileV2();

  const [
    setRightPanelQueueType,
    setLeftPanelQueueType,
    openLeftPanel,
    setOpenLeftPanel,
    showCustomiseYourQueue,
    setShowCustomiseYourQueue,
    setClinicId,
  ] = useQueueStore((state) => [
    state.setRightPanelQueueType,
    state.setLeftPanelQueueType,
    state.openLeftPanel,
    state.setOpenLeftPanel,
    state.showCustomiseYourQueue,
    state.setShowCustomiseYourQueue,
    state.setClinicId,
  ]);
  const daysLeft = useToolPaymentsStore((state) => state.daysLeft);

  const setCalendarConfigs = useQueueStore((state) => state.setCalendarConfigs);
  const setEvitalsConfig = useQueueStore((state) => state.setEvitalsConfig);
  const selectedDoctor = useQueueStore((state) => state.selectedDoctor);
  const setSelectedDoctor = useQueueStore((state) => state.setSelectedDoctor);
  const isAllDoctorsHidden = useQueueStore((state) => state.isAllDoctorsHidden);
  const [
    canShowPurchaseEkaCreditsPopover,
    setCanShowPurchaseEkaCreditsPopover,
  ] = useState(false);
  const [canShowNewSignInStrip, setCanShowNewSignInStrip] = useState(false);
  const [hideRenewalStrip, setHideRenewalStrip] = useState(true);

  const shouldHideReferralStrip = window.shouldHideReferralStrip?.() || false;
  

  useEffect(() => {
    showPurchaseEkaCreditsPopover?.().then((showPurchaseEkaCreditsPopover) => {
      setCanShowPurchaseEkaCreditsPopover(showPurchaseEkaCreditsPopover);
    });

    showNewSignInStrip?.().then((showNewSignInStrip) => {
      setCanShowNewSignInStrip(showNewSignInStrip);
    });

    track("new_queue_dashboard_pageview", { firestore: `true` });

    async function init() {
      const [calendarConfigs, evitalsManual] = await Promise.all([
        getCalendarConfigs(),
        getEvitalsConfig(),
      ]);
      setCalendarConfigs(calendarConfigs);
      setEvitalsConfig(evitalsManual);

      if (cId) {
        if (cId === "all") {
          if (selectedDoctor?.oid) {
            initAptListener({ clinicId: cId, docids: [selectedDoctor?.oid] });
          } else {
            if (isAllDoctorsHidden()) {
              const firstDoctor = getB().getMe()?.docs[0];
              if (firstDoctor) {
                setSelectedDoctor({
                  oid: firstDoctor.id,
                  name: (
                    firstDoctor.personal.name.f +
                    " " +
                    firstDoctor.personal.name.l
                  ).trim(),
                  username: firstDoctor.professional.username,
                  pic: firstDoctor.personal.pic,
                });
                initAptListener({
                  clinicId: cId,
                  docids: [getB().getMe()?.docs[0].id],
                });
              }
            } else {
              if (getB()?.getMe()?.docs?.length > 0) {
                initAptListener({
                  clinicId: cId,
                  docids: getB()
                    .getMe()
                    ?.docs.map((doc) => doc.id),
                });
              } else {
                console.error("queue dashboard: No doctors found in me");
              }
            }
          }
        } else {
          if (isAllDoctorsHidden()) {
            const firstDoctor = getB().getMe()?.docs[0];
            if (firstDoctor) {
              setSelectedDoctor({
                oid: firstDoctor.id,
                name: (
                  firstDoctor.personal.name.f +
                  " " +
                  firstDoctor.personal.name.l
                ).trim(),
                username: firstDoctor.professional.username,
                pic: firstDoctor.personal.pic,
              });
              initAptListener({
                clinicId: cId,
                docids: [getB().getMe()?.docs[0].id],
              });
            }
          } else {
            initAptListener({
              clinicId: cId,
              docids: getB()
                .getMe()
                ?.docs.filter((doc) =>
                  doc.professional.clinics.find((c) => c._id === cId)
                )
                .map((doc) => doc.id),
            });
          }
        }
      }
    }

    init();

    function onEkaEvent(e: Event): void {
      const { detail } = e as CustomEvent<IncomingEvents>;
      switch (detail.type) {
        case REFRESH_APPT: {
          const { selectedDate, clinicId } = useQueueStore.getState();
          queryClient.invalidateQueries(
            getAllQueuesQuery({
              startDate: dateFormat(selectedDate, "yyyy-mm-dd"),
              clinicId,
            })
          );
          break;
        }
        case HIDE_RENEWAL_STRIP: {
          setHideRenewalStrip(detail.shouldHide);
          break;
        }
        default:
          break;
      }
    }

    window.addEventListener(EKA_EVENT, onEkaEvent);

    return () => {
      window.removeEventListener(EKA_EVENT, onEkaEvent);
    };
  }, []);

  useEffect(() => {
    if (_clinicId) {
      setClinicId(_clinicId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cId]);

  const params = new URLSearchParams(window.location.search);

  const leftPanel = params.get("left") as TLeftQueuePanel;
  const rightPanel = params.get("right") as
    | QUEUE_TYPE.CHECKED_IN
    | QUEUE_TYPE.COMPLETED;

  useEffect(() => {
    if (leftPanel) {
      setLeftPanelQueueType(leftPanel);
    } else {
      setLeftPanelQueueType("add-new");
    }
    if (rightPanel) {
      setRightPanelQueueType(rightPanel);
    } else {
      setRightPanelQueueType(QUEUE_TYPE.CHECKED_IN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftPanel, rightPanel]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries(getAllQueuesQuery());
    };
  }, []);

  const onClose = () => {
    setOpenLeftPanel(false);
  };

  const { isDoctorOnly } = useDoctorStaffCheck();

  const isDailyFinanceOverviewStripAllowed = isAccessAvailable(
    SUBJECT_KEYS.DAILY_FINANCE_OVERVIEW_STRIP
  );

  const onAddEkaCredits = () => {
    navigate("push", "/app/add-eka-credits");
    track("ADD_EKA_CREDITS");
  };

  const onAddNewSignIn = () => {
    window.open(
      `${GET_LOGIN_HOST()}/workspace/sign-in/?product_type=emr&add_identity=true&next=${GET_HUB_HOST()}/account/alternate-login-success/`
    );
    track("ADD_NEW_SIGN_IN_STRIP");
  };

  const onCloseNewSignInStrip = () => {
    localStorage.setItem(LS_NEW_SIGN_IN_STRIP, "false");
    setCanShowNewSignInStrip(false);
    track("ADD_NEW_SIGN_IN_STRIP_CLOSE");
  };

  const myProfile = getMe();
  const { pst, pex, ia } = myProfile?.user?.cc || {};
  // const showRenewalStrip =
  //   ia === "t" ||
  //   pst === "false" ||
  //   (pex && pex * 1000 < Date.now()) ||
  //   (pst === "true" && pex && moment(pex * 1000).diff(moment(), "days") <= 30);

  return (
    <>
      <Modal
        style={{
          padding: 0,
          borderRadius: "0.5rem",
          overflow: "hidden",
          minWidth: "80vw",
        }}
        bodyStyle={{ padding: 0 }}
        destroyOnClose
        footer={null}
        closable={false}
        open={showCustomiseYourQueue}
        onCancel={() => {
          setShowCustomiseYourQueue(false);
        }}
      >
        <CustomiseYourQueue />
      </Modal>
      <Col className={`dr-w-full dr-h-full `}>
        <Row className="dr-w-full dr-h-60">
          <QueueHeader />
        </Row>
        <Row className="dr-w-full">
          <ReferralStrip />
        </Row>
        <div id="payment_banner_in_queue" className="dr-mb-16" />
        <div
          className="dr-w-full dr-px-16 dr-bg-darwin-touch-neutral-0"
          style={{
            height: `calc(100% - ${
              daysLeft && daysLeft <= 7 && daysLeft > -1 ? "8rem" : "4.75rem"
            } - ${
              myAccessProfile?.doctors?.length &&
              myAccessProfile.doctors.length > 1
                ? "5.5rem"
                : "0rem"
            } - ${hideRenewalStrip ? "0rem" : "4.25rem"})`,
            
            // } - ${canShowPurchaseEkaCreditsPopover ? "3.5rem" : "0rem"})`,
            // } - ${canShowNewSignInStrip ? "3.125rem" : "0rem"})`,
            // } - ${showRenewalStrip ? "3.25rem" : "0rem"})`,
          }}
        >
          {/* {canShowPurchaseEkaCreditsPopover && (
            <div
              className="flex items-center justify-between px-16 py-8 rounded-8 mb-8 h-48"
              style={{
                background:
                  "linear-gradient(93deg, #19A66A 0%, #75A6CA 97.81%)",
              }}
            >
              <p className="text-14 text-white">
                <span className="font-700">🎁 Doctors’ Day Offer:</span>{" "}
                Recharge & get bonus Eka Credits up to 20% — Only this week!
              </p>
              <button
                className="px-10 py-6 text-12 text-darwin-accent-bright-blue-800 font-700 rounded-4 bg-white"
                onClick={onAddEkaCredits}
              >
                Recharge now
              </button>
            </div>
          )} */}
          {/* {canShowNewSignInStrip && (
            <div
              className="relative flex items-center justify-between mb-8 w-full px-16 py-7 rounded-8 h-42"
              style={{
                background:
                  "linear-gradient(98deg, #215FFF -9.13%, #143999 127.71%)",
              }}
            >
              <div
                onClick={onCloseNewSignInStrip}
                className="absolute py-4 px-4 bg-white -top-10 -right-8 rounded-full border border-darwin-neutral-1000 cursor-pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  className="w-12 h-12 fill-darwin-neutral-1000"
                >
                  <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                </svg>
              </div>
              <div className="flex items-center gap-8">
                <span className="m-4">
                  <svg
                    className="w-14 h-14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.72656 3.26562C8.65039 3.21484 8.625 3.13867 8.625 3.0625C8.625 2.98633 8.65039 2.93555 8.72656 2.88477L10.25 2.25L10.8594 0.751953C10.9102 0.675781 10.9863 0.625 11.0625 0.625C11.1387 0.625 11.1895 0.675781 11.2402 0.751953L11.875 2.25L13.373 2.88477C13.4492 2.93555 13.5 2.98633 13.5 3.0625C13.5 3.13867 13.4492 3.21484 13.373 3.26562L11.875 3.875L11.2402 5.39844C11.1895 5.47461 11.1387 5.5 11.0625 5.5C10.9863 5.5 10.9102 5.47461 10.8594 5.39844L10.25 3.875L8.72656 3.26562ZM13.373 11.0098C13.4492 11.0605 13.5 11.1113 13.5 11.1875C13.5 11.2637 13.4492 11.3398 13.373 11.3906L11.875 12L11.2402 13.5234C11.1895 13.5996 11.1387 13.625 11.0625 13.625C10.9863 13.625 10.9102 13.5996 10.8594 13.5234L10.25 12L8.72656 11.3906C8.65039 11.3398 8.625 11.2637 8.625 11.1875C8.625 11.1113 8.65039 11.0605 8.72656 11.0098L10.25 10.375L10.8594 8.87695C10.9102 8.80078 10.9863 8.75 11.0625 8.75C11.1387 8.75 11.1895 8.80078 11.2402 8.87695L11.875 10.375L13.373 11.0098ZM10.25 7.125C10.25 7.27734 10.1484 7.42969 10.0215 7.48047L7.15234 8.92773L5.73047 11.7969C5.6543 11.9238 5.50195 12 5.375 12C5.19727 12 5.07031 11.9238 4.99414 11.7969L3.57227 8.92773L0.703125 7.48047C0.576172 7.42969 0.5 7.27734 0.5 7.125C0.5 6.97266 0.576172 6.82031 0.703125 6.76953L3.57227 5.32227L4.99414 2.47852C5.14648 2.19922 5.57812 2.19922 5.73047 2.47852L7.15234 5.32227L10.0215 6.76953C10.1484 6.82031 10.25 6.97266 10.25 7.125Z"
                      fill="white"
                    />
                  </svg>
                </span>
                <p className="text-white text-14 font-400 text-white font-700">
                  Faster & safer sign-in method available!
                </p>
              </div>
              <div>
                <button
                  className="bg-white text-darwin-accent-bright-blue-800 px-8 py-4 rounded-4 text-13 font-700"
                  onClick={onAddNewSignIn}
                >
                  Avail now
                </button>
              </div>
            </div>
          )} */}
          {/* <PlanRenewalStrip /> */}
          <DoctorFilter />
          <div className="dr-relative dr-flex dr-rounded-8 dr-w-full dr-h-full">
            <div
              className={`dr-h-full ${
                openLeftPanel
                  ? "dr-bg-darwin-touch-neutral-1000 dr-bg-opacity-40 xl:dr-bg-transparent dr-rounded-8 xl:dr-static dr-absolute dr-w-full xl:dr-w-auto dr-h-full dr-z-10"
                  : "xl:dr-w-0"
              } ${getAclViewClassName(SUBJECT_KEYS.LEFT_BLOCK_ON_NEW_QUEUE)}`}
              onClick={() => {
                onClose();
              }}
            >
              <div
                id="left-panel"
                onClick={(e) => e.stopPropagation()}
                className={`dr-origin-right dr-h-full dr-overflow-hidden ${
                  openLeftPanel
                    ? "dr-bg-darwin-touch-neutral-0 xl:dr-overflow-hidden dr-rounded-tr-8 dr-rounded-br-8 dr-shadow-elevation-300 xl:dr-shadow-none"
                    : "dr-px-16 dr-hidden xl:dr-block"
                } dr-min-w-425 dr-max-w-425 dr-w-425 xl:dr-pr-16`}
              >
                <LeftQueuePanel insideSidePanel={openLeftPanel} />
              </div>
            </div>

            <div id="right-panel" className="dr-px-0 dr-w-full dr-h-full">
              <RightQueuePanel />
            </div>
          </div>
        </div>
        <BottomMarquee
          hideFinanceData={
            !(isDoctorOnly && isDailyFinanceOverviewStripAllowed)
          }
        />
      </Col>
      <ReceiptV2 />
      <HprHfrOnboardingModal />
      <AppointmentLogsDrawer />
      <PackagesAndTreatmentsPopupsAndDrawers />
      <SendReminderModal />
      <SelectSlotModal />
      <SaQuestionnaireDrawer />
      <WorkflowViewJourney />
    </>
  );
};

export default QueueDashboard;
